/**
 * Канонический корпус в формах прототипа.
 *
 * Правило переноса (change `astra-on-canon`): экран Astra не переписывается —
 * меняется источник его данных. Экраны кабинета и управления читают наборы по
 * ИМЕНАМ файлов прототипа (`farms.json`, `works-<cycle>.json`, …), и этот
 * модуль отвечает на те же имена числами сервера.
 *
 * Почему перевод живёт здесь, а не в экранах. Форма прототипа и форма канона
 * расходятся в одном важном месте: прототип держит В СОСТОЯНИИ обязательства
 * сразу две оси — отчитались ли («отчитан», «проверен») и что со сроком
 * («окно идёт», «окно закрылось», «ещё не открылось»). Канон их разделяет:
 * состояние говорит ТОЛЬКО про отчёт, а срок и объявленность живут в полях
 * кампании (`campaign_status`, `is_open`, `overdue`). Свести их обратно — это
 * ровно один перевод, и он обязан быть один на весь продукт; разложенный по
 * экранам, он разошёлся бы на второй же неделе.
 *
 * Чего здесь НЕ делается — счёта. Ни одной доли, ни одного знаменателя этот
 * модуль не выводит: всё, что показывает экран, посчитал сервер. Единственная
 * арифметика — перевод микрогектаров в гектары, потому что прототип хранит
 * площадь в гектарах, а канон — в целых микрогектарах.
 *
 * Чего канон НЕ знает, того здесь не появляется. Категория земли и наличие
 * геометрии у контура хозяйства каноническими выдачами кабинета не отдаются —
 * и подставляются значением `unknown`, которое экран печатает словами «нет
 * данных», а не нулём и не «прочим» (инвариант «отсутствие ≠ ноль»).
 */

import {
  api,
  type ActorDetail,
  type FarmObligationDetail,
  type FarmObligationOut,
  type MyWorks,
  type ObligationContourOut,
  type QueryParams,
  type SeasonCycles,
} from '../../../api/client';
import { cropName } from '../../../lib/crops';
import { haOrOmit, NO_CENTER, toHa } from './dataset';
import type {
  ContourMeta,
  Crop,
  Cycle,
  Farm,
  Meta,
  Obligation,
  ObligationState,
  Work,
  WorkKind,
  WorkStatus,
} from '../../data/types';
import { localIsoDate } from '../../../lib/localDate';
import { defineMessages } from '../../../i18n/define';
import { tr } from '../../../i18n/runtime';

const m = defineMessages({
  farmNo: {
    id: 'data.canon.farmNo',
    defaultMessage: 'Хозяйство #{id}',
    description: 'Название хозяйства, когда сервер имя не назвал; {id} — номер хозяйства',
  },
  district: {
    id: 'data.canon.districtOfParticipant',
    defaultMessage: 'район участника',
    description: 'Название района в сводке среза, когда оно не названо (строчная фраза)',
  },
  seasonNone: {
    id: 'data.canon.seasonNone',
    defaultMessage: 'сезон не назван',
    description: 'Подпись агроцикла-заглушки для работ, у которых сервер не назвал сезон (строчная фраза)',
  },
  noCycles: {
    id: 'data.canon.noCycles',
    defaultMessage: 'районные циклы сервер не отдал: работы собраны вне агроцикла',
    description: 'Оговорка к данным экрана: сервер не прислал агроциклы района, работы показаны вне агроцикла',
  },
  detailCap: {
    id: 'data.canon.detailCap',
    defaultMessage: 'контуры раскрыты у первых {count, plural, one {# работы} other {# работ}}: остальные показаны итогами сервера',
    description: 'Оговорка к данным экрана: контуры подробно загружены только у первых работ; {count} — число работ',
  },
  contoursNoArea: {
    id: 'data.canon.contoursNoArea',
    defaultMessage: 'площадь не названа у {count, plural, one {# контура} other {# контуров}}: в суммы они не вошли',
    description: 'Оговорка к данным экрана: у части контуров нет площади, поэтому в суммах гектаров их нет; {count} — число контуров',
  },
  farmsNoArea: {
    id: 'data.canon.farmsNoArea',
    defaultMessage: 'площадь не названа у {count, plural, one {# хозяйства} other {# хозяйств}}: в суммы они не вошли',
    description: 'Оговорка к данным экрана: у части хозяйств нет площади, поэтому в суммах гектаров их нет; {count} — число хозяйств',
  },
  queueCap: {
    id: 'data.canon.queueCap',
    defaultMessage: 'очередь сервера: показаны первые {shown} из {total} работ по вниманию',
    description: 'Оговорка к данным экрана: очередь внимания обрезана; {shown}/{total} — числа работ',
  },
  queueUnexpanded: {
    id: 'data.canon.queueUnexpanded',
    defaultMessage: 'не раскрылись до контуров {count, plural, one {# работа} few {# работы} many {# работ} other {# работы}} очереди',
    description: 'Оговорка к данным экрана: часть работ очереди внимания не удалось развернуть до контуров; {count} — число работ',
  },
});


/* --------------------------------------------------------------- единицы */

/**
 * Перевод площади живёт в `dataset.ts` — один на все канонические адаптеры.
 * Здесь он только переизлучается, чтобы срез кабинета читался одним модулем.
 */
export { haOrOmit, toHa };

/* ------------------------------------------------------------ имена и ключи */

/**
 * Идентификаторы прототипа — строки, канона — числа. Перевод односторонний и
 * обратимый: строка адреса `/my/report/:cycleId/:workId` обязана вернуться в
 * число, которым пишется отчёт.
 */
export const asId = (value: number): string => String(value);

/** Число из идентификатора набора; `null` — это не канонический идентификатор. */
export function numericId(value: string): number | null {
  if (!/^\d+$/.test(value)) return null;
  return Number(value);
}

/**
 * Идентификатор цикла для адреса. Ключ канона — `«<сезон>:<культура>»`, и
 * двоеточие с тире сезона в адресе экрана читаются плохо, поэтому ключ
 * приводится к безопасному виду.
 */
export function cycleIdOf(seasonLabel: string, cropCode: string | null): string {
  const raw = `${cropCode ?? 'OTHER_CROPS'}-${seasonLabel}`;
  return raw.replace(/[^\w-]+/g, '-');
}

/** Работа вне известного цикла: канон не назвал ни сезона, ни культуры. */
export const NO_CYCLE = 'vne-cikla';

/* ------------------------------------------------------------------ словари */

/**
 * Цвет культуры — имя токена палитры прототипа, а не hex. Незнакомый код
 * получает нейтральный токен и подпись «неизвестно: <код>» из `lib/crops`:
 * подменять незнакомую культуру похожей знакомой запрещено.
 */
const CROP_COLOR: Record<string, string> = {
  COTTON: 'crop-cotton',
  GRAIN: 'crop-wheat',
  POTATO: 'crop-potato',
  FODDER: 'crop-feed',
  RICE: 'crop-other',
  MELON: 'crop-other',
  LEGUMES: 'crop-other',
  OTHER_CROPS: 'crop-other',
};

/** Род работы канона в род работы прототипа. */
const WORK_KIND: Record<string, WorkKind> = {
  tillage: 'tillage',
  sowing: 'sowing',
  irrigation: 'irrigation',
  treatment: 'protection',
  harvest: 'harvest',
  q_inspection: 'other',
  other: 'other',
};

/* ------------------------------------------------------------------- формы */

/** Наборы прототипа, собранные из канона, плюс то, что канон сказал о себе. */
export interface CanonCorpus {
  meta: Meta;
  crops: Crop[];
  cycles: Cycle[];
  farms: Farm[];
  contours: ContourMeta[];
  works: Work[];
  obligations: Obligation[];
  /** Обязательства, по которым отчёт оспорен: хозяйство вправе уточнить. */
  disputed: Set<string>;
  /** Обязательства с действующим отчётом: повторный идёт версией (`amend`). */
  standing: Set<string>;
  /** Работа → канонический `farm_obligation_id` для записи. */
  farmObligationByWork: Map<string, number>;
  /** Кто вошёл: экран управления скрывает суждение тому, кто не проверяющий. */
  canVerify: boolean;
  districtScope: boolean;
  /**
   * Только у оглавления очереди (`queue-index`): циклы, где очередь проверки не
   * пуста. По нему экран выбирает цикл по умолчанию (решение №593).
   */
  queuedCycles?: string[];
  /** Оговорки об охвате и о том, чего канон не отдал. Печатаются словами. */
  notes: string[];
}

/* -------------------------------------------------------- перевод состояний */

/**
 * Состояние обязательства прототипа из двух осей канона.
 *
 * Порядок проверок повторяет `features/cabinet/cabinetModel.ts`: сначала
 * отчёт, потом решение объявить кампанию, и только потом срок. Необъявленная
 * кампания с прошедшим окном — это НЕ молчание хозяйства: от него ещё ничего
 * не просили.
 */
export function obligationState(
  row: ObligationContourOut,
  work: FarmObligationOut,
  asOf: string,
): ObligationState {
  if (row.state === 'confirmed') return 'reported_verified';
  // Спорный отчёт остаётся отчётом: запись хозяйства никуда не делась, а то,
  // что он оспорен, экран берёт из `disputed` и печатает отдельными словами.
  if (
    row.state === 'reported' ||
    row.state === 'needs_clarification' ||
    row.state === 'conflict'
  ) {
    return 'reported';
  }
  // Без отчёта — по окну против среза, как у прототипа (`prototype/data/SCHEMA.md`,
  // obligations: «нет отчёта → done: silent, active: in_window, upcoming:
  // upcoming»). `is_open` канона говорит «от хозяйства ещё ждут», а не «окно
  // накрывает срез»: по нему в идущие окна попадали работы, закрывшиеся
  // сотни дней назад.
  const status = workStatus(work, asOf);
  if (status === 'upcoming') return 'upcoming';
  if (status === 'done') return 'silent';
  return 'in_window';
}

/**
 * Состояние работы прототипа — из окна против среза (`SCHEMA.md`, works:
 * «window.to < as_of → done; окно накрывает as_of → active; window.from >
 * as_of → upcoming»).
 *
 * Два случая канон добавляет сам. Необъявленная кампания — «ещё не
 * объявлена», каким бы ни было окно: от хозяйства ещё ничего не просили.
 * Работа без окна (условная, «на отстающих полях») срока не имеет: пока канон
 * ждёт по ней отчёта (`is_open`), она идёт, но ни «закрывается сегодня», ни
 * «ближайшим сроком» экран её не называет — окна у неё нет.
 */
export function workStatus(work: FarmObligationOut, asOf: string): WorkStatus {
  if (work.campaign_status === 'planned') return 'upcoming';
  const from = work.window_from;
  const to = work.window_to;
  if (to !== null && (work.window_elapsed === true || to < asOf)) return 'done';
  if (from !== null && from > asOf) return 'upcoming';
  if (from !== null || to !== null) return 'active';
  return work.is_open ? 'active' : 'done';
}

/** Дата независимой проверки: последнее подтверждение по этой записи. */
function verifiedOn(row: ObligationContourOut): string | null {
  let found: string | null = null;
  for (const claim of row.verifications) {
    if (claim.outcome !== 'confirmed') continue;
    if (found === null || claim.occurred_on > found) found = claim.occurred_on;
  }
  return found;
}

/**
 * Последнее суждение проверки, оспорившее отчёт (опровержение или просьба
 * уточнить). Причина — примечание проверяющего как есть; пустое — `null`.
 */
export function disputeOf(row: ObligationContourOut): NonNullable<Obligation['dispute']> | null {
  let found: ObligationContourOut['verifications'][number] | null = null;
  for (const claim of row.verifications) {
    if (claim.outcome === 'confirmed') continue;
    if (found === null || claim.entry_time > found.entry_time) found = claim;
  }
  if (!found) return null;
  return {
    by: found.verifier,
    on: found.occurred_on,
    reason: found.note.trim() || null,
    outcome: found.outcome === 'rejected' ? 'rejected' : 'needs_clarification',
  };
}

/** Отчёт оспорен: проверка попросила уточнить или опровергла его. */
function isDisputed(row: ObligationContourOut): boolean {
  return row.state === 'needs_clarification' || row.state === 'conflict';
}

/* ------------------------------------------------------------------ сборка */

/**
 * Раскрытие работы до контуров: то, что корпус берёт из карточки работы
 * (`/farm-obligations/{id}`) или из раскрытия всех работ разом
 * (`/my/works/contours`) — норматив и контуры, ключ — id работы.
 */
export type WorkExpansion = Pick<FarmObligationDetail, 'norm' | 'contours'> & {
  item: { id: number };
};

export interface CanonSources {
  /** Список работ: «мои работы» или работы, собранные из очереди проверки. */
  works: Pick<MyWorks, 'context' | 'principal' | 'items' | 'scope_note'>;
  details: WorkExpansion[];
  seasons: SeasonCycles | null;
  actors: ActorDetail[];
  /** Сколько работ раскрыто до контуров; `null` — все. */
  detailCap: number | null;
  /** Оговорки источника, которых нет в самом ответе (например, страница очереди). */
  notes?: string[];
}

function buildCrops(seasons: SeasonCycles | null, placed: Set<string>): Crop[] {
  const byCode = new Map<string, Crop>();
  const add = (code: string, name?: string): void => {
    if (byCode.has(code)) return;
    byCode.set(code, {
      id: code,
      name: cropName(code, name),
      color: CROP_COLOR[code] ?? 'crop-other',
    });
  };
  for (const row of seasons?.items ?? []) add(row.crop_code, row.crop_name);
  for (const code of placed) add(code);
  return [...byCode.values()];
}

function buildCycles(seasons: SeasonCycles | null): {
  cycles: Cycle[];
  byKey: Map<string, string>;
} {
  const cycles: Cycle[] = [];
  const byKey = new Map<string, string>();
  for (const row of seasons?.items ?? []) {
    const id = cycleIdOf(row.season_label, row.crop_code);
    byKey.set(`${row.season_label}|${row.crop_code}`, id);
    cycles.push({
      id,
      template_id: row.cycle,
      crop: row.crop_code,
      season_label: row.season_label,
      status: row.status === 'active' ? 'active' : 'closed',
      window: { from: row.window_from ?? '', to: row.window_to ?? '' },
      farms: row.actors.total,
      contours: row.contours.total,
      area_ha: haOrOmit(row.area.total_microha),
      stage_now: row.stage?.name ?? null,
      progress: {
        works_total: row.campaigns,
        // Ход цикла считает сервер; раскладывать кампании на «сделано» и
        // «идёт» в браузере значило бы завести второй счёт рядом с его.
        works_done: 0,
        works_active: 0,
        reported_pct: 0,
        verified_pct: 0,
      },
    });
  }
  return { cycles, byKey };
}

/** Сезонные размещения контуров хозяйства: последний сезон и предыдущий. */
function placementsOf(actor: ActorDetail): {
  current: Map<number, string>;
  previous: Map<number, string>;
  codes: Set<string>;
} {
  const blocks = [...actor.placement_details].sort((a, b) =>
    a.season < b.season ? 1 : a.season > b.season ? -1 : 0,
  );
  const codes = new Set<string>();
  const pick = (index: number): Map<number, string> => {
    const map = new Map<number, string>();
    for (const row of blocks[index]?.rows ?? []) {
      map.set(row.contour_id, row.crop_code);
      codes.add(row.crop_code);
    }
    return map;
  };
  return { current: pick(0), previous: pick(1), codes };
}

function buildFarmAndContours(actor: ActorDetail): {
  farm: Farm;
  contours: ContourMeta[];
  codes: Set<string>;
  /** Канон назвал площадь хозяйства. `false` — ноль в `area_ha` не утверждение. */
  areaStated: boolean;
} {
  const { current, previous, codes } = placementsOf(actor);
  const massifIds = new Set<string>();
  const seen = new Map<number, ContourMeta>();

  for (const use of actor.land_uses) {
    massifIds.add(asId(use.massif.id));
    const stated = toHa(use.total_microha);
    const known = seen.get(use.contour_id);
    if (known) {
      // Контур, поделённый на части, остаётся ОДНИМ контуром: площади частей
      // складываются, а строка списка не удваивается. Часть без названной
      // площади не прибавляет нуля — она снимает у строки право звать сумму
      // площадью контура.
      known.area_ha += stated ?? 0;
      if (stated === null) known.area_stated = false;
      continue;
    }
    seen.set(use.contour_id, {
      id: asId(use.contour_id),
      no: use.contour_no,
      massif_id: asId(use.massif.id),
      farm_id: asId(actor.id),
      area_ha: stated ?? 0,
      // Площадь названа источником? `false` — ноль в `area_ha` не утверждение,
      // и экран обязан сказать «нет данных», а не «0 га».
      area_stated: stated !== null,
      // Категория земли и геометрия каноническими выдачами кабинета не
      // отдаются. `unknown` — это «не знаем», и экран печатает его словами.
      category: 'unknown',
      crop_2026: current.get(use.contour_id) ?? null,
      crop_2025w: previous.get(use.contour_id) ?? null,
      geometry: 'unknown',
      // Нули поставили бы контур в Гвинейский залив; центра у кабинета нет.
      center: NO_CENTER,
    });
  }

  const contours = [...seen.values()];
  const farmArea = toHa(actor.total_microha);
  const farm: Farm = {
    id: asId(actor.id),
    name: actor.name ?? tr(m.farmNo, { id: actor.id }),
    massif_ids: [...massifIds],
    direction: actor.directions[0]?.raw_label ?? '',
    area_ha: farmArea ?? 0,
    contours: actor.contours,
    head: '',
  };
  return { farm, contours, codes, areaStated: farmArea !== null };
}

function buildWork(
  item: FarmObligationOut,
  detail: WorkExpansion | undefined,
  cycleByKey: Map<string, string>,
  asOf: string,
): Work {
  const norm = detail?.norm ?? null;
  const cycleId =
    norm && norm.template_season_label
      ? (cycleByKey.get(`${norm.template_season_label}|${norm.template_crop_code ?? ''}`) ??
        cycleIdOf(norm.template_season_label, norm.template_crop_code ?? null))
      : NO_CYCLE;
  const total = item.obligations.total;
  const reported = item.obligations.reported;
  const elapsed = workStatus(item, asOf) === 'done';
  return {
    id: asId(item.id),
    cycle_id: cycleId,
    stage: norm?.slot_label ?? item.campaign_title,
    name: item.work_name,
    kind: WORK_KIND[item.work_category] ?? 'other',
    seq: norm?.slot_order_no ?? 0,
    window: { from: item.window_from ?? '', to: item.window_to ?? '' },
    status: workStatus(item, asOf),
    totals: {
      obligations: total,
      area_ha: haOrOmit(item.area.total_microha),
      reported,
      verified: item.obligations.verified,
      // Опоздание отчёта канон отдельно не считает: «отчитались позже срока»
      // выводится из дат, а не из состояния, и выдумывать его здесь нельзя:
      // `null` — «не утверждается», а не «ни одного» (agroai-6al.27).
      late: null,
      silent: elapsed ? Math.max(total - reported, 0) : 0,
    },
    note: null,
    overdue: item.overdue,
    ...(item.campaign_kind && item.campaign_kind !== 'plan' ? { source: workSource(item.campaign_kind) } : {}),
  };
}

function workSource(kind: string): NonNullable<Work['source']> {
  return kind === 'prescription' || kind === 'program' || kind === 'unplanned_water' ? kind : 'plan';
}

/**
 * Дата среза: названная контекстом, а без неё — «сегодня» СЕРВЕРА («как знаем
 * сейчас»): `horizon` списка циклов, который читается вместе со списком работ.
 * Часы браузера — последняя страховка, когда циклы не прочитались: с ними
 * экраны кабинета говорили бы «срез 11 окт» и «на срезе 12 окт» (разные
 * полночи сервера и вкладки).
 */
function sliceDate(source: CanonSources['works'], seasons: SeasonCycles | null): string {
  return source.context.as_of ?? seasons?.horizon ?? localIsoDate();
}

function buildMeta(source: CanonSources['works'], seasons: SeasonCycles | null, farms: Farm[], contours: number): Meta {
  return {
    track: 'B',
    as_of: sliceDate(source, seasons),
    district: tr(m.district),
    seed: 0,
    generated_at: '',
    sources: [],
    totals: {
      massifs: 0,
      farms: farms.length,
      contours,
      agri_area_ha: farms.reduce((sum, farm) => sum + farm.area_ha, 0),
      mapped_contours: 0,
    },
  };
}

/** Цикл-заглушка для работ, у которых канон не назвал сезона и культуры. */
function outsideCycle(): Cycle {
  return {
    id: NO_CYCLE,
    template_id: '',
    crop: 'OTHER_CROPS',
    season_label: tr(m.seasonNone),
    status: 'active',
    window: { from: '', to: '' },
    farms: 0,
    contours: 0,
    area_ha: 0,
    stage_now: null,
    progress: {
      works_total: 0,
      works_done: 0,
      works_active: 0,
      reported_pct: 0,
      verified_pct: 0,
    },
  };
}

export function buildCorpus(sources: CanonSources): CanonCorpus {
  // Срез, против которого читаются окна: тот же, что уходит в `meta.as_of`.
  const asOf = sliceDate(sources.works, sources.seasons);
  const { cycles, byKey } = buildCycles(sources.seasons);
  const detailById = new Map<number, WorkExpansion>();
  for (const detail of sources.details) detailById.set(detail.item.id, detail);

  const farms: Farm[] = [];
  const contours: ContourMeta[] = [];
  const placedCodes = new Set<string>();
  let farmsWithoutArea = 0;
  for (const actor of sources.actors) {
    const built = buildFarmAndContours(actor);
    if (!built.areaStated) farmsWithoutArea += 1;
    farms.push(built.farm);
    contours.push(...built.contours);
    for (const code of built.codes) placedCodes.add(code);
  }
  // Хозяйства, по которым пришли работы, но карточка не запрашивалась: имя
  // берётся из самой работы, иначе очередь показала бы голый идентификатор.
  const known = new Set(farms.map((farm) => farm.id));
  for (const item of sources.works.items) {
    const id = asId(item.actor.id);
    if (known.has(id)) continue;
    known.add(id);
    farms.push({
      id,
      name: item.actor.name ?? tr(m.farmNo, { id: item.actor.id }),
      massif_ids: [],
      direction: '',
      area_ha: 0,
      contours: 0,
      head: '',
    });
  }

  const works: Work[] = [];
  const obligations: Obligation[] = [];
  const disputed = new Set<string>();
  const standing = new Set<string>();
  const farmObligationByWork = new Map<string, number>();

  for (const item of sources.works.items) {
    const detail = detailById.get(item.id);
    const work = buildWork(item, detail, byKey, asOf);
    works.push(work);
    farmObligationByWork.set(work.id, item.id);
    const demo = item.epistemic_status === 'synthetic_demo';

    for (const row of detail?.contours ?? []) {
      const id = asId(row.obligation_id);
      const contested = isDisputed(row);
      if (contested) disputed.add(id);
      if (row.progress !== null) standing.add(id);
      obligations.push({
        id,
        ...(contested ? { disputed: true } : {}),
        ...(contested && disputeOf(row) ? { dispute: disputeOf(row)! } : {}),
        ...(row.progress === 'in_progress' && row.state !== 'confirmed' ? { in_progress: true } : {}),
        work_id: work.id,
        farm_id: asId(item.actor.id),
        contour_id: asId(row.contour_id),
        contour_no: row.contour_no,
        ...(row.contour_part_id != null ? { contour_part_id: asId(row.contour_part_id) } : {}),
        area_ha: haOrOmit(row.area_microha),
        state: obligationState(row, item, asOf),
        reported_on: row.reported_on,
        verified_on: verifiedOn(row),
        // «Опоздал» канон не утверждает: см. `Work.totals.late`.
        late: false,
        reported_by: row.reported_by,
        on_behalf: row.entered_on_behalf ? (item.actor.name ?? asId(item.actor.id)) : null,
        ...(demo ? { evidence: 'demo' as const } : {}),
      });
    }
  }

  const notes: string[] = [...(sources.notes ?? [])];
  if (sources.seasons === null) {
    notes.push(tr(m.noCycles));
  }
  if (sources.detailCap !== null) {
    notes.push(tr(m.detailCap, { count: sources.detailCap }));
  }
  if (sources.works.scope_note) notes.push(sources.works.scope_note);
  // Неназванная площадь в суммы не вошла — и это говорится словами, потому что
  // молчащий ноль неотличим от измеренного нуля.
  const contoursWithoutArea = contours.filter((row) => row.area_stated === false).length;
  if (contoursWithoutArea > 0) {
    notes.push(tr(m.contoursNoArea, { count: contoursWithoutArea }));
  }
  if (farmsWithoutArea > 0) {
    notes.push(tr(m.farmsNoArea, { count: farmsWithoutArea }));
  }

  return {
    meta: buildMeta(sources.works, sources.seasons, farms, contours.length),
    crops: buildCrops(sources.seasons, placedCodes),
    cycles: works.some((work) => work.cycle_id === NO_CYCLE)
      ? [...cycles, outsideCycle()]
      : cycles,
    farms,
    contours,
    works,
    obligations,
    disputed,
    standing,
    farmObligationByWork,
    canVerify: sources.works.principal.can_verify,
    districtScope: sources.works.principal.district_scope,
    notes,
  };
}

/* ------------------------------------------------------------------ загрузка */

/**
 * Сколько работ раскрывается до контуров.
 *
 * Кабинету хозяйства потолок не ставится: своё хозяйство раскрывается целиком.
 * Управлению района он нужен: раскрыть все работы района значило бы сделать
 * сотни запросов на открытие одного экрана. Потолок назван числом и попадает
 * в оговорки корпуса, а не молча обрезает выдачу.
 */
export const DETAIL_CAP = 60;

/** Чем набран список работ корпуса (см. `corpus.ts`). */
export type CorpusBasis = 'works' | 'queue' | 'queue-index' | 'index';

/**
 * Срез запроса кабинета и управления — ровно срез контекста анализа.
 *
 * Флаг демо здесь НЕ подменяется: правило одно для всех экранов — галка
 * «Показывать демо-данные» соблюдается везде, а по умолчанию она включена
 * (`analysis/context.tsx`: стенд целиком синтетический, и выключенное
 * умолчание давало аккаунту хозяйства ноль работ при 78 на сервере). Строки
 * демо не теряют пометки — обязательство несёт `evidence: 'demo'`. Запись идёт
 * тем же срезом, что и чтение: иначе сервер не нашёл бы демо-обязательство,
 * которое экран показал.
 */
export function canonParams(params: QueryParams): QueryParams {
  return { ...params };
}

/**
 * Очередь проверки района: работы, по которым есть отчёт без суждения.
 *
 * Порядок — серверный («по вниманию»: просрочка вперёд, затем ближний срок),
 * страница — первые `DETAIL_CAP` строк; каждая раскрывается до контуров
 * карточкой `/farm-obligations/{id}`, потому что суждение пишется по
 * контурному обязательству. Фильтры вида (`actor_id`, `massif_id`) уходят на
 * сервер: ими проверяющий находит своё хозяйство за пределами первой страницы.
 */
async function loadQueueCorpus(query: QueryParams): Promise<CanonCorpus> {
  // Ось цикла (`cycle`) сужает ТОЛЬКО очередь: на сервере она стоит до
  // страницы «по вниманию», и иначе работы закрытого сезона занимали бы всю
  // первую страницу. Список циклов и карточки работ читаются без неё —
  // экрану нужен весь список циклов, а карточке работы ось ни к чему.
  const { cycle: _cycle, ...plain } = query;
  // Очередь и список циклов друг от друга не зависят — один круг, а не два
  // (agroai-0e8.26): цепочка «очередь → циклы → карточки» стоила экрану
  // проверки лишнего ожидания на каждом открытии.
  //
  // Раскрытие до контуров ждёт только страницу очереди, а не список циклов
  // (agroai-b48): после суждения `/season/cycles` считается заново, и
  // карточки работ, ждавшие его, шли лишней волной после него.
  const queueTask = api.verificationQueue({
    ...query,
    only_awaiting: true,
    limit: DETAIL_CAP,
  });
  const detailsTask = queueTask.then((page) =>
    Promise.all(page.items.map((row) => api.farmObligation(row.farm_obligation_id, plain).catch(() => null))),
  );
  const [queue, seasons, expanded] = await Promise.all([
    queueTask,
    api.seasonCycles(plain).catch(() => null),
    detailsTask,
  ]);
  const details = expanded.filter((row): row is FarmObligationDetail => row !== null);

  const notes: string[] = [];
  if (queue.page.total > queue.items.length) {
    notes.push(tr(m.queueCap, { shown: queue.items.length, total: queue.page.total }));
  }
  if (details.length < queue.items.length) {
    notes.push(tr(m.queueUnexpanded, { count: queue.items.length - details.length }));
  }
  return buildCorpus({
    works: {
      context: queue.context,
      principal: queue.principal,
      items: details.map((detail) => detail.item),
      scope_note: queue.empty_note ?? null,
    },
    details,
    seasons,
    actors: [],
    detailCap: null,
    notes,
  });
}

/**
 * Оглавление очереди: срез, циклы района и то, в каких из них очередь не
 * пуста. Работ не раскрывает — по одному лёгкому запросу очереди на цикл
 * (`limit=1`, отвечает итогом страницы). Работы шаблонов без культуры циклом
 * не являются: если район ждёт суждения по ним, оглавление добавляет «вне
 * цикла», и экран читает их страницей района без оси.
 */
async function loadQueueIndex(query: QueryParams, probe: boolean): Promise<CanonCorpus> {
  const [head, seasons] = await Promise.all([
    api.verificationQueue({ ...query, only_awaiting: true, limit: 1 }),
    api.seasonCycles(query).catch(() => null),
  ]);
  const probes = await Promise.all(
    (probe ? (seasons?.items ?? []) : []).map((row) =>
      api
        .verificationQueue({ ...query, only_awaiting: true, cycle: row.cycle, limit: 1 })
        .then((answer) => ({ id: cycleIdOf(row.season_label, row.crop_code), total: answer.page.total }))
        .catch(() => null),
    ),
  );
  const known = probes.filter((row): row is { id: string; total: number } => row !== null);
  const queuedCycles = known.filter((row) => row.total > 0).map((row) => row.id);
  const inCycles = known.reduce((sum, row) => sum + row.total, 0);
  const outside = probe && seasons !== null && known.length === seasons.items.length && head.page.total > inCycles;
  const built = buildCorpus({
    works: {
      context: head.context,
      principal: head.principal,
      items: [],
      scope_note: head.empty_note ?? null,
    },
    details: [],
    seasons,
    actors: [],
    detailCap: null,
  });
  return {
    ...built,
    cycles: outside ? [...built.cycles, outsideCycle()] : built.cycles,
    queuedCycles: outside ? [...queuedCycles, NO_CYCLE] : queuedCycles,
  };
}

export async function loadCanonCorpus(
  params: QueryParams,
  basis: CorpusBasis = 'works',
): Promise<CanonCorpus> {
  const query = canonParams(params);
  if (basis === 'queue') return loadQueueCorpus(query);
  if (basis === 'queue-index') return loadQueueIndex(query, true);
  if (basis === 'index') return loadQueueIndex(query, false);
  return loadWorksCorpus(query);
}

async function loadWorksCorpus(params: QueryParams): Promise<CanonCorpus> {
  // Три чтения кабинета друг от друга не зависят и уходят ОДНИМ кругом
  // (agroai-0e8.26): прежде список работ, циклы и раскрытие до контуров шли
  // цепочкой из трёх ожиданий подряд. Раскрытие спрашивается сразу — та же
  // область, что у `/my/works`, — а отбирается по списку работ ниже.
  const worksTask = api.myWorks({ ...params, only_open: false });
  // Циклы района — отдельный вопрос, и его отказ не должен ронять кабинет:
  // без сезонов работы соберутся «вне цикла», и это будет сказано словами.
  const seasonsTask = api.seasonCycles(params).catch(() => null);
  // Отказ раскрытия не роняет кабинет — работы остаются без контуров.
  const expandedTask = api.myWorksContours(params).catch(() => null);
  const works = await worksTask;

  // Потолок — только для районной области. У хозяйства стенда 78 работ, и
  // идущие окна стоят в выдаче после 60-й: под потолком кабинет раскрывал
  // закрытые работы и говорил «окон нет». Своё хозяйство раскрывается целиком
  // (карточка работы — десятки миллисекунд); под потолком идущие — первыми.
  // Районная роль, выбравшая ОДНО хозяйство (`actor_id`, ввод за хозяйство),
  // смотрит его целиком, как само хозяйство: под потолком закрытые работы с
  // недостающими отчётами не раскрылись бы до контуров.
  const wide = works.principal.district_scope && params.actor_id == null;
  const items = wide
    ? [...works.items].sort((a, b) => Number(b.is_open) - Number(a.is_open))
    : works.items;
  const capped = wide && items.length > DETAIL_CAP;
  const wanted = capped ? items.slice(0, DETAIL_CAP) : items;
  // Все работы раскрываются ОДНИМ вызовом (`/my/works/contours`, agroai-0e8):
  // по карточке на работу кабинет фермера делал 144 запроса и тянул 11 МБ.
  const wantedIds = new Set(wanted.map((item) => item.id));

  // Карточки хозяйств нужны ради земли и размещений. У района их сотни, и
  // управление района землёй одного хозяйства не занимается — поэтому
  // карточки берутся только для СВОИХ хозяйств участника.
  const actorIds = works.principal.district_scope ? [] : works.actors.map((actor) => actor.id);
  const [seasons, expanded, actors] = await Promise.all([
    seasonsTask,
    expandedTask,
    Promise.all(actorIds.map((id) => api.actor(id, params).catch(() => null))),
  ]);
  const details: WorkExpansion[] = wanted.length
    ? (expanded?.items ?? [])
        .filter((row) => wantedIds.has(row.id))
        .map((row) => ({ item: { id: row.id }, norm: row.norm, contours: row.contours }))
    : [];

  return buildCorpus({
    works,
    details,
    seasons,
    actors: actors.filter((row): row is ActorDetail => row !== null),
    detailCap: capped ? DETAIL_CAP : null,
  });
}
